import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Popover,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { Check as CheckIcon } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { ReactComponent as CalendarIcon } from '../../../assets/PepWise2.0/Calendar.svg';
import { ReactComponent as CalendarIconActive } from '../../../assets/PepWise2.0/Calendar-Active.svg';
import { ReactComponent as RightArrowBoxIcon } from '../../../assets/PepWise2.0/Right-Arrow-Icon-Box.svg';
import { ReactComponent as LeftArrowBoxIcon } from '../../../assets/PepWise2.0/Left-Arrow-Icon-Box.svg';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type Props = {
  selectedMonth: string;
  onChange: (value: string, isAllMonths?: boolean) => void;
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;
  hideAllMonths?: boolean;
};

export default function GenericMonthFilter({
  selectedMonth,
  onChange,
  minDate,
  maxDate,
  hideAllMonths = false,
}: Props) {
  const theme = useTheme();
  const colors = theme.palette;

    const isYearOnly = (value: string) => {
        return value.length === 4 && !isNaN(Number(value));
    };

  const parsedSelectedMonth = useMemo(() => {
    if (!selectedMonth) return null;

    if (isYearOnly(selectedMonth)) {
      const year = Number(selectedMonth);
      return dayjs().year(year).startOf('year');
    }

    return dayjs(selectedMonth).startOf('month');
  }, [selectedMonth]);

  const isAllMonths = useMemo(() => {
    return Boolean(selectedMonth && isYearOnly(selectedMonth));
  }, [selectedMonth]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [viewYear, setViewYear] = useState(parsedSelectedMonth?.year() || dayjs().year());

  const open = Boolean(anchorEl);

  const effectiveMinDate = useMemo(() => {
    return minDate ? minDate.startOf('month') : null;
  }, [minDate]);

  const effectiveMaxDate = useMemo(() => {
    const currentMonth = dayjs().startOf('month');

    if (!maxDate) return currentMonth;

    const maxMonth = maxDate.startOf('month');
    return maxMonth.isAfter(currentMonth, 'month') ? currentMonth : maxMonth;
  }, [maxDate]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setViewYear(parsedSelectedMonth?.year() || effectiveMaxDate.year());
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isMonthDisabled = (date: Dayjs) => {
    if (effectiveMinDate && date.isBefore(effectiveMinDate, 'month')) return true;
    if (date.isAfter(effectiveMaxDate, 'month')) return true;
    return false;
  };

  const isAllMonthsDisabled = useMemo(() => {
    const yearStart = dayjs().year(viewYear).startOf('year');
    const yearEnd = dayjs().year(viewYear).month(11).startOf('month');

    if (effectiveMinDate && yearEnd.isBefore(effectiveMinDate, 'month')) return true;
    if (yearStart.isAfter(effectiveMaxDate, 'month')) return true;

    return false;
  }, [viewYear, effectiveMinDate, effectiveMaxDate]);

  const handleAllMonthsToggle = (checked: boolean) => {
    if (!checked) {
      onChange('', false);
      return;
    }

    onChange(`${viewYear}`, true);
    handleClose();
  };

  const handleMonthClick = (monthIndex: number) => {
    const newDate = dayjs().year(viewYear).month(monthIndex).startOf('month');

    if (isMonthDisabled(newDate)) return;

    onChange(newDate.format('YYYY-MM'), false);
    handleClose();
  };

  const disablePrevYear = useMemo(() => {
    if (!effectiveMinDate) return false;

    return dayjs()
      .year(viewYear - 1)
      .month(11)
      .startOf('month')
      .isBefore(effectiveMinDate, 'month');
  }, [viewYear, effectiveMinDate]);

  const disableNextYear = useMemo(() => {
    return dayjs()
      .year(viewYear + 1)
      .month(0)
      .startOf('month')
      .isAfter(effectiveMaxDate, 'month');
  }, [viewYear, effectiveMaxDate]);

  const displayValue = useMemo(() => {
    if (!selectedMonth || !parsedSelectedMonth) return 'Select Month';
    if (isAllMonths) return `${parsedSelectedMonth.format('YYYY')}, All Months`;

    return parsedSelectedMonth.format('MMM YYYY');
  }, [selectedMonth, parsedSelectedMonth, isAllMonths]);

  const getMonthBackgroundColor = (isActive: boolean, isDisabled: boolean) => {
    if (isDisabled) return 'transparent';
    if (isActive) return colors.dateFilter.rangeBackground;

    return 'transparent';
  };

  const getMonthTextColor = (isActive: boolean, isDisabled: boolean) => {
    if (isDisabled) return colors.dateFilter.dayText;
    if (isActive) return colors.dateFilter.dayTextInRange;

    return colors.dateFilter.dayText;
  };

  const getMonthHoverBackgroundColor = (isActive: boolean, isDisabled: boolean) => {
    if (isDisabled) return 'transparent';
    if (isActive) return colors.dateFilter.rangeBackground;

    return colors.dateFilter.dayHoverBackground;
  };

  const getMonthHoverTextColor = (isActive: boolean, isDisabled: boolean) => {
    if (isDisabled) return colors.dateFilter.dayText;
    if (isActive) return colors.dateFilter.dayTextInRange;

    return colors.dateFilter.dayHoverText;
  };

  return (
    <Box>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: `${colors.dateFilter.triggerHoverBackground} !important`,
          },
        }}
      >
        <Typography
          fontSize="14px"
          sx={{
            color: open ? colors.dateFilter.triggerTextActive : colors.dateFilter.triggerText,
          }}
        >
          {displayValue}
        </Typography>
        {open ? <CalendarIconActive /> : <CalendarIcon />}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              backgroundColor: `${colors.dateFilter.popoverBackground} !important`,
              color: `${colors.dateFilter.popoverText} !important`,
              borderRadius: '8px',
              boxShadow: colors.dateFilter.popoverShadow,
              display: 'flex',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            width: 260,
            background: `${colors.dateFilter.calendarBackground} !important`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <IconButton
              size="small"
              disabled={disablePrevYear}
              onClick={() => setViewYear(viewYear - 1)}
            >
              <RightArrowBoxIcon />
            </IconButton>

            <Typography
              sx={{
                fontWeight: 600,
                color: colors.dateFilter.calendarHeaderText,
                fontSize: '16px',
                fontFamily: 'Montserrat',
              }}
            >
              {viewYear}
            </Typography>

            <IconButton
              size="small"
              disabled={disableNextYear}
              onClick={() => setViewYear(viewYear + 1)}
            >
              <LeftArrowBoxIcon />
            </IconButton>
          </Box>

          {!hideAllMonths && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllMonths}
                    disabled={isAllMonthsDisabled}
                    onChange={(e) => handleAllMonthsToggle(e.target.checked)}
                    icon={
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          border: `1.5px solid ${colors.dateFilter.rangeBackground}`,
                          borderRadius: '4px',
                        }}
                      />
                    }
                    checkedIcon={
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          border: `1.5px solid ${colors.dateFilter.rangeBackground}`,
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckIcon sx={{ color: colors.dateFilter.rangeBackground, fontSize: 18 }} />
                      </Box>
                    }
                    sx={{ padding: '4px 9px' }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: colors.dateFilter.rangeBackground,
                      fontWeight: 500,
                      fontFamily: 'Montserrat',
                    }}
                  >
                    All Months
                  </Typography>
                }
              />
            </Box>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
            }}
          >
            {months.map((month, index) => {
              const monthDate = dayjs().year(viewYear).month(index).startOf('month');
              const isDisabled = isMonthDisabled(monthDate);
              const isActive =
                !isDisabled &&
                ((isAllMonths && parsedSelectedMonth?.year() === viewYear) ||
                  (!isAllMonths &&
                    parsedSelectedMonth?.year() === viewYear &&
                    parsedSelectedMonth?.month() === index));

              return (
                <Box
                  key={month}
                  onClick={() => handleMonthClick(index)}
                  sx={{
                    height: 55,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: getMonthTextColor(isActive, isDisabled),
                    bgcolor: getMonthBackgroundColor(isActive, isDisabled),
                    fontFamily: 'Montserrat',
                    transition: 'all 0.2s',
                    opacity: isDisabled ? 0.35 : 1,
                    '&:hover': {
                      bgcolor: getMonthHoverBackgroundColor(isActive, isDisabled),
                      color: getMonthHoverTextColor(isActive, isDisabled),
                    },
                  }}
                >
                  {month}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}